/**
 * Pazar ayarları (market_settings) için TEK KAYNAK: panel formu ve API doğrulaması bunu kullanır.
 * Yeni ayar = DBML'de sütun + buraya bir satır. `live: false` = kod henüz kullanmıyor (panelde "yakında").
 * Değerler: süreler tam sayı; yüzdeler bps (1000 = %10); para kuruş (metin); puan 0–5.
 */
export type SettingUnit =
  | "minutes"
  | "hours"
  | "days"
  | "count"
  | "bps"
  | "money"
  | "rating"
  | "boolean"
  | "time"
  | "minutes_list"
  | "meters"
  | "hours_decimal";

export interface SettingDef {
  key: string;
  group: SettingGroup;
  label: string;
  help?: string;
  unit: SettingUnit;
  min?: number;
  max?: number;
  /** boş bırakılabilir (DB'de null) */
  nullable?: boolean;
  /** kod bu ayarı kullanıyor mu */
  live: boolean;
  /** panelden değişmez (ör. lansmanda kapalı özellik, vergi gösterimi) */
  readOnly?: boolean;
  /** yalnızca super admin değiştirir (güvenlik eşiği) */
  superAdminOnly?: boolean;
  decision?: string;
}

export const SETTING_GROUPS = [
  "booking",
  "cancellation",
  "payments",
  "payouts",
  "urgent",
  "quality",
  "safety",
  "marketing",
] as const;
export type SettingGroup = (typeof SETTING_GROUPS)[number];

export const SETTING_GROUP_LABEL: Record<SettingGroup, string> = {
  booking: "Rezervasyon ve faturalama",
  cancellation: "İptal",
  payments: "Ödeme ve tahsilat",
  payouts: "Hizmet veren ödemeleri",
  urgent: "Acil çağrı ve takip",
  quality: "Kalite ve hesap",
  safety: "Güvenlik ve itiraz",
  marketing: "Pazarlama ve site",
};

export const MARKET_SETTINGS: readonly SettingDef[] = [
  // rezervasyon
  {
    key: "min_billable_minutes",
    group: "booking",
    label: "En az faturalanan süre",
    unit: "minutes",
    min: 15,
    max: 480,
    live: true,
    decision: "K-021",
  },
  {
    key: "billing_increment_minutes",
    group: "booking",
    label: "Faturalama adımı",
    help: "Süre bu adıma yuvarlanır",
    unit: "minutes",
    min: 1,
    max: 60,
    live: true,
    decision: "K-037",
  },
  {
    key: "provider_max_min_minutes",
    group: "booking",
    label: "Hizmet verenin seçebileceği en yüksek minimum süre",
    unit: "minutes",
    min: 15,
    max: 480,
    live: false,
  },
  {
    key: "invoice_submit_hours",
    group: "booking",
    label: "İş bitince fatura gönderme süresi",
    unit: "hours",
    min: 1,
    max: 168,
    live: false,
  },
  {
    key: "auto_confirm_hours",
    group: "booking",
    label: "Müşteri onaylamazsa otomatik onay",
    help: "Bu süre sonunda fatura onaylanır ve tahsil edilir",
    unit: "hours",
    min: 1,
    max: 168,
    live: true,
    decision: "K-013",
  },
  {
    key: "confirm_reminder_minutes",
    group: "booking",
    label: "Onay hatırlatmaları",
    help: "Faturadan kaç dakika sonra hatırlatılır (virgülle)",
    unit: "minutes_list",
    min: 5,
    max: 10080,
    live: true,
    decision: "K-013",
  },
  {
    key: "invitation_expiry_hours",
    group: "booking",
    label: "İş davetinin geçerlilik süresi",
    unit: "hours",
    min: 1,
    max: 168,
    nullable: true,
    live: true,
  },
  {
    key: "tip_cap_bps",
    group: "booking",
    label: "Bahşiş üst sınırı",
    unit: "bps",
    min: 0,
    max: 10000,
    nullable: true,
    live: true,
    decision: "K-028",
  },
  // iptal
  {
    key: "free_cancel_hours",
    group: "cancellation",
    label: "Ücretsiz iptal sınırı",
    help: "İşe bu kadar saat kalana kadar ücretsiz",
    unit: "hours",
    min: 0,
    max: 336,
    live: true,
    decision: "K-022",
  },
  {
    key: "cancellation_fee_hours",
    group: "cancellation",
    label: "Geç iptal ücreti (saat ücreti cinsinden)",
    unit: "hours_decimal",
    min: 0,
    max: 8,
    nullable: true,
    live: true,
    decision: "K-022",
  },
  {
    key: "cancellation_fee_bps",
    group: "cancellation",
    label: "Sabit fiyatlı işte geç iptal ücreti",
    help: "Menü tutarının yüzdesi; tamamı hizmet verene, üstüne Poji ücreti",
    unit: "bps",
    min: 0,
    max: 10000,
    nullable: true,
    live: true,
    decision: "K-058",
  },
  {
    key: "provider_cancel_compensation_hours",
    group: "cancellation",
    label: "Hizmet veren iptalinde müşteriye tazminat (saat)",
    unit: "hours_decimal",
    min: 0,
    max: 8,
    nullable: true,
    live: true,
  },
  // ödeme
  {
    key: "hold_before_start_hours",
    group: "payments",
    label: "Kartta provizyon zamanı",
    help: "İşe bu kadar saat kala tutar kartta bloke edilir",
    unit: "hours",
    min: 1,
    max: 168,
    live: true,
    decision: "K-017",
  },
  {
    key: "hold_extra_bps",
    group: "payments",
    label: "Provizyon güvenlik payı",
    unit: "bps",
    min: 0,
    max: 5000,
    nullable: true,
    live: true,
    decision: "K-017",
  },
  {
    key: "unapproved_expense_minor",
    group: "payments",
    label: "Onaysız masraf sınırı",
    help: "Hizmet veren bu tutara kadar küçük masrafı (deterjan, vida) fişiyle faturaya yazar; üstü için müşteriden uygulamada onay ister",
    unit: "money",
    live: true,
    decision: "K-053",
  },
  {
    key: "payment_fix_hours",
    group: "payments",
    label: "Kart sorununu düzeltme süresi",
    help: "Provizyon başarısızsa; sonra iş iptal",
    unit: "hours",
    min: 1,
    max: 72,
    live: true,
    decision: "K-075",
  },
  {
    key: "prices_include_tax",
    group: "payments",
    label: "Fiyatlar KDV dahil",
    unit: "boolean",
    live: true,
    readOnly: true,
    decision: "K-041",
  },
  {
    key: "cash_enabled",
    group: "payments",
    label: "Nakit ödeme",
    unit: "boolean",
    live: false,
    readOnly: true,
    decision: "K-045",
  },
  {
    key: "cash_debt_limit_minor",
    group: "payments",
    label: "Nakit komisyon borcu sınırı",
    unit: "money",
    min: 0,
    max: 1_000_000,
    nullable: true,
    live: false,
    decision: "K-045",
  },
  // ödemeler
  {
    key: "payout_delay_days",
    group: "payouts",
    label: "Yeni hizmet verene ödeme günü",
    unit: "days",
    min: 0,
    max: 30,
    live: true,
    decision: "K-018",
  },
  {
    key: "trusted_payout_delay_days",
    group: "payouts",
    label: "Güvenilir hizmet verene ödeme günü",
    unit: "days",
    min: 0,
    max: 30,
    live: true,
    decision: "K-018",
  },
  {
    key: "first_payout_min_days",
    group: "payouts",
    label: "İlk ödeme en erken",
    unit: "days",
    min: 7,
    max: 30,
    live: false,
    decision: "K-018",
  },
  {
    key: "trusted_min_completed_tasks",
    group: "payouts",
    label: "Güvenilir sayılmak için tamamlanan iş",
    unit: "count",
    min: 0,
    max: 1000,
    live: false,
    decision: "K-018",
  },
  {
    key: "trusted_min_days",
    group: "payouts",
    label: "Güvenilir sayılmak için gün",
    unit: "days",
    min: 0,
    max: 365,
    live: false,
  },
  {
    key: "trusted_min_rating",
    group: "payouts",
    label: "Güvenilir sayılmak için puan",
    unit: "rating",
    min: 0,
    max: 5,
    nullable: true,
    live: false,
  },
  // acil
  {
    key: "urgent_accept_timeout_minutes",
    group: "urgent",
    label: "Acil davete cevap süresi",
    unit: "minutes",
    min: 1,
    max: 120,
    live: true,
    decision: "K-050",
  },
  {
    key: "urgent_max_arrival_minutes",
    group: "urgent",
    label: "Acilde en geç varış",
    unit: "minutes",
    min: 10,
    max: 240,
    live: true,
    decision: "K-050",
  },
  {
    key: "tracking_lead_minutes",
    group: "urgent",
    label: "Erişim notu görünür ve canlı konum önerilir (işe kala)",
    unit: "minutes",
    min: 0,
    max: 240,
    live: true,
    decision: "K-051",
  },
  {
    key: "arrival_radius_m",
    group: "urgent",
    label: "Varış sayılan mesafe",
    unit: "meters",
    min: 20,
    max: 2000,
    live: false,
  },
  {
    key: "recipient_wait_minutes",
    group: "urgent",
    label: "Kurye: alıcıyı bekleme",
    unit: "minutes",
    min: 0,
    max: 60,
    live: false,
    decision: "K-086",
  },
  // kalite
  {
    key: "quality_min_rating",
    group: "quality",
    label: "Kalite uyarısı puan sınırı",
    unit: "rating",
    min: 0,
    max: 5,
    live: false,
    decision: "K-080",
  },
  {
    key: "quality_window_tasks",
    group: "quality",
    label: "Kalite için bakılan son iş sayısı",
    unit: "count",
    min: 1,
    max: 200,
    live: false,
    decision: "K-080",
  },
  {
    key: "quality_max_cancel_bps",
    group: "quality",
    label: "En yüksek iptal oranı",
    unit: "bps",
    min: 0,
    max: 10000,
    live: false,
    decision: "K-080",
  },
  {
    key: "quality_max_no_shows_90d",
    group: "quality",
    label: "90 günde en çok gelmeme",
    unit: "count",
    min: 0,
    max: 20,
    live: false,
    decision: "K-080",
  },
  {
    key: "termination_notice_days",
    group: "quality",
    label: "Hesap kapatma öncesi bildirim",
    unit: "days",
    min: 30,
    max: 365,
    live: false,
    decision: "P2B",
  },
  // güvenlik
  {
    key: "dispute_window_hours",
    group: "safety",
    label: "Sorun bildirme süresi (iş bitince)",
    unit: "hours",
    min: 1,
    max: 720,
    live: true,
    decision: "K-047",
  },
  {
    key: "refund_dual_approval_minor",
    group: "safety",
    label: "Dört göz onayı gereken iade tutarı",
    help: "Bu tutarın üstündeki iade ikinci yöneticinin onayını ister",
    unit: "money",
    min: 0,
    max: 10_000_000,
    live: true,
    superAdminOnly: true,
    decision: "K-095",
  },
  // pazarlama
  {
    key: "seo_min_providers",
    group: "marketing",
    label: "Sayfa indekslenmesi için en az hizmet veren",
    unit: "count",
    min: 0,
    max: 100,
    live: false,
    decision: "P-12",
  },
  {
    key: "seo_min_reviews",
    group: "marketing",
    label: "Sayfa indekslenmesi için en az yorum",
    unit: "count",
    min: 0,
    max: 100,
    live: false,
    decision: "P-12",
  },
  {
    key: "marketing_max_per_week",
    group: "marketing",
    label: "Kişi başı haftalık pazarlama mesajı",
    unit: "count",
    min: 0,
    max: 14,
    live: false,
    decision: "P-18",
  },
  {
    key: "marketing_quiet_start",
    group: "marketing",
    label: "Sessiz saat başlangıcı",
    unit: "time",
    nullable: true,
    live: false,
  },
  {
    key: "marketing_quiet_end",
    group: "marketing",
    label: "Sessiz saat bitişi",
    unit: "time",
    nullable: true,
    live: false,
  },
  {
    key: "app_banner_enabled",
    group: "marketing",
    label: "Mobil webde “Uygulamada aç” bandı",
    unit: "boolean",
    live: false,
    decision: "P-20",
  },
];

export type SettingValue = number | string | boolean | number[] | null;

/** v, 1/per adımlarında mı (kayan nokta payıyla: 4.35 * 100 = 434.99999…) */
const steps = (v: number, per: number) => Math.abs(Math.round(v * per) - v * per) < 1e-6;

/** Tek ayarın değerini doğrular; hata kodu ya da null döner */
export function checkSetting(def: SettingDef, v: unknown): string | null {
  if (v === null) return def.nullable ? null : "required";
  const inRange = (n: number) =>
    (def.min === undefined || n >= def.min) && (def.max === undefined || n <= def.max);
  switch (def.unit) {
    case "boolean":
      return typeof v === "boolean" ? null : "boolean";
    case "time":
      return typeof v === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(v) ? null : "time";
    case "money":
      if (typeof v !== "string" || !/^\d{1,12}$/.test(v)) return "money";
      return inRange(Number(v)) ? null : "range";
    case "rating":
      if (typeof v !== "number" || !steps(v, 100)) return "number";
      return inRange(v) ? null : "range";
    case "hours_decimal":
      if (typeof v !== "number" || !steps(v, 4)) return "quarter_hours";
      return inRange(v) ? null : "range";
    case "minutes_list":
      if (!Array.isArray(v) || v.length > 10 || v.some((n) => !Number.isInteger(n))) return "list";
      if (v.some((n: number) => !inRange(n))) return "range";
      return v.every((n: number, i) => i === 0 || n > v[i - 1]!) ? null : "ascending";
    default:
      if (typeof v !== "number" || !Number.isInteger(v)) return "integer";
      return inRange(v) ? null : "range";
  }
}

/** Ayarlar arası tutarlılık (yeni değerler + mevcutlar birleşik) */
export function checkSettingsTogether(s: Record<string, SettingValue>): string[] {
  const out: string[] = [];
  const n = (k: string) => (typeof s[k] === "number" ? (s[k] as number) : null);
  if ((n("trusted_payout_delay_days") ?? 0) > (n("payout_delay_days") ?? Infinity))
    out.push("trusted_payout_after_regular");
  if ((n("min_billable_minutes") ?? 0) > (n("provider_max_min_minutes") ?? Infinity))
    out.push("min_billable_over_provider_max");
  if ((n("billing_increment_minutes") ?? 1) > (n("min_billable_minutes") ?? Infinity))
    out.push("increment_over_min_billable");
  return out;
}
